// ============================================
// COMPANYSURVEYRESULTSPAGE.JSX - Full-page Survey Results View
// ============================================

import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetCompanyByIdQuery,
  useGetSurveyResultsQuery,
} from "../../../services/api";
import "./CompanyDetails.css";
import "./CompanySurveys.css";

// ============================================
// ICONS
// ============================================
const IconResponses = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconPercent = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="5" x2="5" y2="19" />
    <circle cx="6.5" cy="6.5" r="2.5" />
    <circle cx="17.5" cy="17.5" r="2.5" />
  </svg>
);

const IconQuestions = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 1 1 5.82 1c0 2-3 2-3 4" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// Color palette for chart bars
const BAR_COLORS = ["#6366f1", "#8b5cf6", "#a78bfa", "#c4b5fd", "#ddd6fe"];

/**
 * CompanySurveyResultsPage Component
 * Full-page view for survey results with clear stats and submissions
 */
export default function CompanySurveyResultsPage() {
  const { id: companyId, templateId } = useParams();
  const navigate = useNavigate();

  // ==================== API ====================
  const { data: companyData } = useGetCompanyByIdQuery(companyId);
  const companyName = companyData?.name || "Loading...";

  const { data: results, isLoading, error } = useGetSurveyResultsQuery(
    { templateId, companyId },
    { skip: !templateId || !companyId }
  );

  // ==================== UI STATE ====================
  const [activeTab, setActiveTab] = useState("overview"); // 'overview' | 'responses'
  const [expandedSubmission, setExpandedSubmission] = useState(null);

  const toggleSubmission = (id) => {
    setExpandedSubmission(expandedSubmission === id ? null : id);
  };

  // ==================== COMPUTED ====================
  const completionRate =
    results && results.totalEmployees > 0
      ? Math.round((results.submissionCount / results.totalEmployees) * 100)
      : 0;

  const questionCount = results?.questions?.length || 0;

  // ==================== FORMAT ANSWER ====================
  const formatAnswerValue = (answer) => {
    if (answer.value === null || answer.value === undefined || answer.value === "") {
      return <span className="sr-no-answer">Not answered</span>;
    }
    switch (answer.questionType) {
      case "single_choice":
        return answer.valueLabel || answer.value;
      case "multi_choice":
        if (answer.valueLabels && answer.valueLabels.length > 0) {
          return (
            <div className="sr-multi-tags">
              {answer.valueLabels.map((l, i) => (
                <span key={i} className="sr-multi-tag">{l}</span>
              ))}
            </div>
          );
        }
        if (Array.isArray(answer.value)) {
          return answer.value.join(", ");
        }
        return String(answer.value);
      case "rating_scale":
        return (
          <div className="sr-rating-inline">
            <span className="sr-rating-number">{answer.value}</span>
            <span className="sr-rating-max">/ 5</span>
          </div>
        );
      default:
        return String(answer.value);
    }
  };

  // ==================== RENDER QUESTION CARD ====================
  const renderQuestionCard = (question, idx) => {
    const { questionType, stats, responseCount, responses, questionText } = question;

    return (
      <div key={idx} className="sr-question-card">
        {/* Question header */}
        <div className="sr-question-top">
          <div className="sr-question-index">Q{question.questionIndex + 1}</div>
          <div className="sr-question-body">
            <div className="sr-question-text">{questionText}</div>
            <span className="sr-question-type-badge">{questionType.replace("_", " ")}</span>
          </div>
          <div className="sr-question-response-count">
            {responseCount} {responseCount === 1 ? "response" : "responses"}
          </div>
        </div>

        {/* Rating scale */}
        {questionType === "rating_scale" && stats && (
          <div className="sr-rating-section">
            <div className="sr-rating-avg-block">
              <div className="sr-rating-avg-number">{stats.average}</div>
              <div className="sr-rating-avg-label">Average Rating</div>
              <div className="sr-rating-avg-range">out of {stats.max || 5}</div>
            </div>
            {stats.distribution && (
              <div className="sr-rating-bars">
                {Object.entries(stats.distribution)
                  .sort(([a], [b]) => Number(b) - Number(a))
                  .map(([rating, count]) => {
                    const pct = responseCount > 0 ? (count / responseCount) * 100 : 0;
                    return (
                      <div key={rating} className="sr-bar-row">
                        <span className="sr-bar-label">{rating}</span>
                        <div className="sr-bar-track">
                          <div
                            className="sr-bar-fill sr-bar-fill-rating"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="sr-bar-value">{count} ({Math.round(pct)}%)</span>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}

        {/* Single / Multi choice */}
        {(questionType === "single_choice" || questionType === "multi_choice") && stats?.distribution && (
          <div className="sr-choice-section">
            {(() => {
              const entries = Object.entries(stats.distribution).sort(([, a], [, b]) => b - a);
              const total = entries.reduce((sum, [, c]) => sum + c, 0);
              return (
                <div className="sr-choice-bars">
                  {entries.map(([label, count], i) => {
                    const pct = total > 0 ? (count / total) * 100 : 0;
                    return (
                      <div key={label} className="sr-bar-row">
                        <span className="sr-bar-label sr-bar-label-wide">{label}</span>
                        <div className="sr-bar-track">
                          <div
                            className="sr-bar-fill"
                            style={{
                              width: `${pct}%`,
                              background: BAR_COLORS[i % BAR_COLORS.length],
                            }}
                          />
                        </div>
                        <span className="sr-bar-value">{count} ({Math.round(pct)}%)</span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {/* Text responses */}
        {(questionType === "text_short" || questionType === "text_long") && (
          <div className="sr-text-section">
            {(() => {
              const textResponses = responses?.filter((r) => r !== null && r !== "") || [];
              if (textResponses.length === 0) {
                return <div className="sr-text-empty">No text responses yet.</div>;
              }
              return (
                <div className="sr-text-list">
                  {textResponses.map((response, i) => (
                    <div key={i} className="sr-text-bubble">
                      <div className="sr-text-content">{response}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  // ==================== LOADING / ERROR ====================
  if (isLoading) {
    return (
      <div className="cd-company-details">
        <div className="welcome" onClick={() => navigate(`/admin/companies/${companyId}/surveys`)}>
          &larr; Back to Surveys
        </div>
        <div className="cd-loading" style={{ padding: "60px 0", textAlign: "center" }}>Loading results...</div>
      </div>
    );
  }

  if (error || (!isLoading && !results)) {
    return (
      <div className="cd-company-details">
        <div className="welcome" onClick={() => navigate(`/admin/companies/${companyId}/surveys`)}>
          &larr; Back to Surveys
        </div>
        <div className="cd-empty-state" style={{ padding: "60px 0" }}>
          <p>Failed to load survey results.</p>
        </div>
      </div>
    );
  }

  // ==================== MAIN RENDER ====================
  return (
    <div className="cd-company-details">
      {/* Breadcrumb */}
      <div className="welcome" onClick={() => navigate(`/admin/companies/${companyId}/surveys`)}>
        &larr; Back to {companyName} Surveys
      </div>

      {/* Page Title */}
      <div className="sr-page-header">
        <h1 className="username">{results.template?.title || "Survey Results"}</h1>
        {results.template?.isAnonymous && (
          <span className="sr-anonymous-pill">Anonymous</span>
        )}
      </div>

      {/* Summary Cards */}
      <div className="sr-summary-grid">
        <div className="sr-summary-card">
          <div className="sr-summary-icon sr-summary-icon-blue"><IconResponses /></div>
          <div className="sr-summary-info">
            <div className="sr-summary-number">{results.submissionCount}</div>
            <div className="sr-summary-label">Responses</div>
          </div>
        </div>
        <div className="sr-summary-card">
          <div className="sr-summary-icon sr-summary-icon-green"><IconPercent /></div>
          <div className="sr-summary-info">
            <div className="sr-summary-number">{completionRate}%</div>
            <div className="sr-summary-label">Completion Rate</div>
          </div>
        </div>
        <div className="sr-summary-card">
          <div className="sr-summary-icon sr-summary-icon-purple"><IconQuestions /></div>
          <div className="sr-summary-info">
            <div className="sr-summary-number">{questionCount}</div>
            <div className="sr-summary-label">Questions</div>
          </div>
        </div>
        <div className="sr-summary-card">
          <div className="sr-summary-icon sr-summary-icon-gray"><IconResponses /></div>
          <div className="sr-summary-info">
            <div className="sr-summary-number">{results.totalEmployees}</div>
            <div className="sr-summary-label">Total Employees</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sr-tabs">
        <button
          className={`sr-tab ${activeTab === "overview" ? "sr-tab-active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          Question Overview
        </button>
        <button
          className={`sr-tab ${activeTab === "responses" ? "sr-tab-active" : ""}`}
          onClick={() => setActiveTab("responses")}
        >
          Individual Responses ({results.submissions?.length || 0})
        </button>
      </div>

      {/* ==================== OVERVIEW TAB ==================== */}
      {activeTab === "overview" && (
        <div className="sr-questions-list">
          {results.questions && results.questions.length > 0 ? (
            results.questions.map((q, idx) => renderQuestionCard(q, idx))
          ) : (
            <div className="sr-empty-block">
              <p>No responses have been submitted yet.</p>
            </div>
          )}
        </div>
      )}

      {/* ==================== RESPONSES TAB ==================== */}
      {activeTab === "responses" && (
        <div className="sr-responses-list">
          {results.submissions && results.submissions.length > 0 ? (
            results.submissions.map((submission, sIdx) => (
              <div key={submission._id} className="sr-response-card">
                {/* Response header */}
                <div
                  className="sr-response-header"
                  onClick={() => toggleSubmission(submission._id)}
                >
                  <div className="sr-response-avatar">
                    {results.template?.isAnonymous
                      ? "?"
                      : submission.employee
                        ? submission.employee.firstName?.charAt(0).toUpperCase()
                        : "#"}
                  </div>
                  <div className="sr-response-info">
                    <div className="sr-response-name">
                      {results.template?.isAnonymous
                        ? `Response #${sIdx + 1}`
                        : submission.employee
                          ? `${submission.employee.firstName} ${submission.employee.lastName}`
                          : "Unknown Employee"}
                    </div>
                    <div className="sr-response-meta">
                      {!results.template?.isAnonymous && submission.employee?.email && (
                        <span className="sr-response-email">{submission.employee.email}</span>
                      )}
                      <span className="sr-response-date">
                        {submission.submittedAt
                          ? new Date(submission.submittedAt).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "In progress"}
                      </span>
                    </div>
                  </div>
                  <div className={`sr-response-chevron ${expandedSubmission === submission._id ? "sr-response-chevron-open" : ""}`}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>

                {/* Expanded answers */}
                {expandedSubmission === submission._id && submission.answers && (
                  <div className="sr-response-answers">
                    {submission.answers.map((answer, aIdx) => (
                      <div key={answer._id || aIdx} className="sr-response-answer">
                        <div className="sr-response-q-label">
                          Q{answer.questionIndex + 1}. {answer.questionText}
                        </div>
                        <div className="sr-response-a-value">
                          {formatAnswerValue(answer)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="sr-empty-block">
              <p>No individual responses yet.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
